import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useReveal from "@/hooks/useReveal";
import { Link } from "react-router-dom";

const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_afrqa-luxury/artifacts/31u5d5k2_3eb31fc8-5e84-49b0-81ae-8fee1cb47ade.JPG";

const PILLARS = [
  {
    no: "I.",
    title: "Future Culture",
    body: "Heritage carried forward — in materials and silhouettes built for the decade to come.",
  },
  {
    no: "II.",
    title: "Wearable Identity",
    body: "Every piece is a quiet statement of self. Worn, never performed.",
  },
  {
    no: "III.",
    title: "Elevated Design",
    body: "We make less, and we make it well. Every edition numbered. Every detail considered.",
  },
  {
    no: "IV.",
    title: "Technology-Inspired",
    body: "Form follows future. Precision engineering — without ever feeling like a gadget.",
  },
];

export default function AboutPage() {
  const r1 = useReveal();
  const r2 = useReveal();
  const r3 = useReveal();

  return (
    <div data-testid="about-page" className="bg-[#0a0a0a] text-white">
      <Header />
      <main className="pt-28 md:pt-36 pb-24">
        {/* Hero */}
        <section className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div
            ref={r1.ref}
            className={`reveal ${r1.inView ? "in-view" : ""} grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-end`}
          >
            <div className="lg:col-span-7">
              <span className="font-label text-[#d4af37]">— About AFRQA™ —</span>
              <h1 className="mt-5 font-display text-[clamp(2.6rem,6vw,5.4rem)] leading-[0.96] tracking-[-0.025em]">
                Future culture.
                <br />
                <span className="font-editorial italic gold-text-gradient">
                  Wearable identity.
                </span>
              </h1>
              <p className="mt-8 max-w-xl text-[#cfcfca] text-lg leading-relaxed font-light">
                A Los Angeles design studio at the intersection of culture
                and futurism. Objects that look forward — without forgetting
                where they came from.
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="relative zoom-wrap prototype-photo aspect-[4/5] bg-[#0c0c0c] border border-[#1f1f1f]">
                <img
                  src={HERO_IMAGE}
                  alt="AFRQA editorial portrait"
                  className="w-full h-full object-cover"
                />
                <span
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.45) 100%)",
                  }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Pillars */}
        <section className="mt-28 md:mt-36 border-y border-[#1a1a1a] bg-[#070707] py-20 md:py-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div
              ref={r2.ref}
              className={`reveal ${r2.inView ? "in-view" : ""} mb-14 max-w-3xl`}
            >
              <span className="font-label text-[#d4af37]">
                — Four Principles —
              </span>
              <h2 className="mt-4 font-display text-[clamp(1.8rem,4vw,3.4rem)] leading-tight">
                What AFRQA{" "}
                <span className="font-editorial italic gold-text-gradient">
                  stands for.
                </span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
              {PILLARS.map((p, i) => (
                <div
                  key={p.title}
                  className="group border border-[#1f1f1f] bg-[#0c0c0c] p-8 md:p-10 hover:border-[#d4af37]/50 transition-colors"
                  data-testid={`about-pillar-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-editorial italic gold-text-gradient text-3xl leading-none">
                      {p.no}
                    </span>
                    <span className="font-label text-[#6a6a65]">
                      0{i + 1} / 04
                    </span>
                  </div>
                  <h3 className="mt-8 font-display text-2xl md:text-3xl text-white">
                    {p.title}
                  </h3>
                  <p className="mt-4 text-[#a3a3a3] leading-relaxed font-light">
                    {p.body}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Phase II teaser */}
        <section
          ref={r3.ref}
          className={`reveal ${r3.inView ? "in-view" : ""} mt-28 max-w-[1400px] mx-auto px-6 md:px-10 text-center`}
        >
          <span className="font-label text-[#d4af37]">— Phase II —</span>
          <h2 className="mt-4 font-display text-[clamp(2rem,5vw,4rem)] leading-[1.05] max-w-3xl mx-auto">
            The future speaks{" "}
            <span className="font-editorial italic gold-text-gradient">
              your language.
            </span>
          </h2>
          <p className="mt-6 text-[#a3a3a3] max-w-xl mx-auto font-light">
            Adaptive Vision. Future Systems. Loading.
          </p>
          <Link
            to="/vision"
            data-testid="about-phase-2-cta"
            className="btn-ghost-gold mt-10 inline-flex"
          >
            Read the Manifesto
          </Link>
        </section>
      </main>
      <Footer />
    </div>
  );
}
